import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
// Reusable components
import AnalyticsDashboard from "@/components/analytics/AnalyticsDashboard";
import CustomLinksEditor from "@/components/email/CustomLinksEditor";
import {
  ArrowLeft,
  Download,
  Users,
  Mail,
  Phone,
  Globe,
  MapPin,
  Loader2,
  BarChart3,
  Smartphone,
  Monitor,
  RefreshCw,
  Send,
  Trash2,
  Save,
  Target,
  FileCode,
  Eye,
  Search,
  Pencil,
  Check,
  X,
  TrendingUp,
  Link2,
  BarChart2,
  MousePointerClick,
  ExternalLink,
  Calendar,
  Clock,
  Percent
} from "lucide-react";

// Default HTML email template
const DEFAULT_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #f97316; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .highlight { color: #f97316; font-weight: bold; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Opportunità per la tua attività</h1>
    </div>
    <div class="content">
      <p>Gentile <span class="highlight">{nome}</span>,</p>
      
      <p>Mi chiamo Max Valle e sono un consulente di marketing digitale specializzato nell'aiutare le aziende a migliorare la propria presenza online.</p>
      
      <p>Ho avuto modo di analizzare il vostro sito web e ho notato alcune opportunità di miglioramento che potrebbero incrementare significativamente la vostra visibilità sui motori di ricerca.</p>
      
      <p>Saresti interessato ad una breve consulenza gratuita per scoprire come ottimizzare la tua presenza online?</p>
      
      <p>Cordiali saluti,<br>
      <strong>Max Valle</strong><br>
      MaxValle.it</p>
    </div>
    <div class="footer">
      <p>Max Valle - Consulente Marketing Digitale</p>
    </div>
  </div>
</body>
</html>`;

const LeadGroupDetailPage = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const groupId = searchParams.get("id");
  
  // State
  const [group, setGroup] = useState(null);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leads");
  
  // Email template state
  const [emailSubject, setEmailSubject] = useState("Opportunità per la tua attività");
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_TEMPLATE);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  
  // Custom link placeholders
  const [customLinks, setCustomLinks] = useState({
    tracking: "",
    video: "",
    consulenza: "",
    risorsa: "",
    chiamami: ""
  });
  
  // SEO Analysis state
  const [analyzingWebsite, setAnalyzingWebsite] = useState(null);
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  
  // Email extraction state
  const [extractingEmail, setExtractingEmail] = useState(null);
  const [bulkExtracting, setBulkExtracting] = useState(false);
  
  // Email editing state
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [editingEmailValue, setEditingEmailValue] = useState("");
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Test email state
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  // Helper function to extract error message safely (handles Pydantic validation errors)
  const getErrorMessage = (error, fallback = "Errore") => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
    return fallback;
  };
  
  // User settings (for calendar links)
  const [userSettings, setUserSettings] = useState({
    calendar_link_bni: "",
    calendar_link_standard: ""
  });
  
  useEffect(() => {
    if (groupId) {
      loadGroupDetail();
      loadUserSettings();
    }
  }, [groupId]);
  
  const loadUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setUserSettings({
          calendar_link_bni: response.data.calendar_link_bni || response.data.calendar_link || "",
          calendar_link_standard: response.data.calendar_link_standard || ""
        });
      }
    } catch (error) {
      console.log("No user settings found");
    }
  };
  
  const loadGroupDetail = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/lead-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGroup(response.data);
      setLeads(response.data.leads || []);
      setEmailSubject(response.data.email_template_subject || "Opportunità per la tua attività");
      setEmailBody(response.data.email_template_body || DEFAULT_EMAIL_TEMPLATE);
      
      // Load custom links
      if (response.data.custom_links) {
        setCustomLinks({
          tracking: response.data.custom_links.tracking || "",
          video: response.data.custom_links.video || "",
          consulenza: response.data.custom_links.consulenza || "",
          risorsa: response.data.custom_links.risorsa || "",
          chiamami: response.data.custom_links.chiamami || ""
        });
      }
    } catch (error) {
      console.error("Error loading group:", error);
      toast.error("Errore nel caricamento del gruppo");
      navigate("/leads");
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadAnalytics = async () => {
    setIsLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API}/analytics/group/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
      // Don't show error if just no data yet
    } finally {
      setIsLoadingAnalytics(false);
    }
  };
  
  const exportAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${group?.name || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Report esportato!");
    } catch (error) {
      console.error("Error exporting analytics:", error);
      toast.error("Errore nell'esportazione");
    }
  };
  
  const handleSaveTemplate = async () => {
    setIsSavingTemplate(true);
    try {
      await axios.put(`${API}/lead-groups/${groupId}`, {
        name: group.name,
        email_template_subject: emailSubject,
        email_template_body: emailBody,
        custom_links: customLinks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Template email salvato");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Errore nel salvataggio del template");
    } finally {
      setIsSavingTemplate(false);
    }
  };
  
  const handleExtractEmail = async (lead) => {
    if (!lead.website) {
      toast.warning("Questo lead non ha un sito web");
      return;
    }
    
    setExtractingEmail(lead._id);
    
    try {
      const response = await axios.post(`${API}/leads/extract-email`, {
        lead_id: lead._id,
        website: lead.website
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.email) {
        setLeads(prev => prev.map(l => 
          l._id === lead._id ? { ...l, email: response.data.email } : l
        ));
        toast.success(`Email trovata: ${response.data.email}`);
      } else {
        toast.info("Nessuna email trovata su questo sito");
      }
    } catch (error) {
      console.error("Extract email error:", error);
      toast.error("Errore nell'estrazione dell'email");
    } finally {
      setExtractingEmail(null);
    }
  };
  
  const handleBulkExtractEmails = async () => {
    const leadsWithWebsite = leads.filter(l => l.website && !l.email);
    
    if (leadsWithWebsite.length === 0) {
      toast.info("Tutti i lead hanno già un'email o non hanno un sito web");
      return;
    }
    
    setBulkExtracting(true);
    toast.info(`Estrazione email da ${leadsWithWebsite.length} siti...`);
    
    try {
      const leadIds = leadsWithWebsite.map(l => l._id);
      const response = await axios.post(`${API}/leads/extract-emails-bulk`, leadIds, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const results = response.data.results || [];
      setLeads(prev => prev.map(lead => {
        const result = results.find(r => r.lead_id === lead._id);
        if (result && result.email) {
          return { ...lead, email: result.email };
        }
        return lead;
      }));
      
      toast.success(`Trovate ${response.data.found} email su ${response.data.total} siti`);
    } catch (error) {
      console.error("Bulk extract error:", error);
      toast.error("Errore nell'estrazione delle email");
    } finally {
      setBulkExtracting(false);
    }
  };
  
  const handleStartEditEmail = (lead) => {
    setEditingEmailId(lead._id);
    setEditingEmailValue(lead.email || "");
  };
  
  const handleCancelEditEmail = () => {
    setEditingEmailId(null);
    setEditingEmailValue("");
  };
  
  const handleSaveEmail = async (leadId) => {
    try {
      await axios.put(`${API}/leads/${leadId}/email`, {
        email: editingEmailValue.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(prev => prev.map(l => 
        l._id === leadId ? { ...l, email: editingEmailValue.trim() } : l
      ));
      
      toast.success("Email salvata");
      setEditingEmailId(null);
      setEditingEmailValue("");
    } catch (error) {
      console.error("Save email error:", error);
      toast.error("Errore nel salvataggio dell'email");
    }
  };
  
  const handleAnalyzeSEO = async (lead) => {
    if (!lead.website) {
      toast.warning("Questo lead non ha un sito web");
      return;
    }
    
    setAnalyzingWebsite(lead._id);
    
    try {
      const response = await axios.post(`${API}/leads/analyze-seo`, {
        lead_id: lead._id,
        website: lead.website
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(prev => prev.map(l => 
        l._id === lead._id ? { ...l, seo_data: response.data.seo_data } : l
      ));
      
      toast.success("Analisi SEO completata");
    } catch (error) {
      console.error("SEO analysis error:", error);
      toast.error("Errore nell'analisi SEO");
    } finally {
      setAnalyzingWebsite(null);
    }
  };
  
  const handleBulkAnalyze = async () => {
    const leadsWithWebsite = leads.filter(l => l.website && !l.seo_data?.domain);
    
    if (leadsWithWebsite.length === 0) {
      toast.info("Tutti i lead sono già stati analizzati");
      return;
    }
    
    setBulkAnalyzing(true);
    let analyzed = 0;
    
    for (const lead of leadsWithWebsite) {
      try {
        const response = await axios.post(`${API}/leads/analyze-seo`, {
          lead_id: lead._id,
          website: lead.website
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setLeads(prev => prev.map(l => 
          l._id === lead._id ? { ...l, seo_data: response.data.seo_data } : l
        ));
        
        analyzed++;
      } catch (error) {
        console.error(`Error analyzing ${lead.website}:`, error);
      }
      
      await new Promise(r => setTimeout(r, 1000));
    }
    
    setBulkAnalyzing(false);
    toast.success(`Analizzati ${analyzed} siti web`);
  };
  
  const handleOpenEmailDialog = (lead) => {
    if (!lead.email) {
      toast.warning("Questo lead non ha un indirizzo email. Estrai o inserisci prima l'email.");
      return;
    }
    setSelectedLead(lead);
    setEmailDialogOpen(true);
  };
  
  const handleSendEmail = async () => {
    if (!selectedLead?.email) {
      toast.error("Questo lead non ha un indirizzo email");
      return;
    }
    
    setIsSendingEmail(true);
    
    try {
      // Get SEO data from lead
      const seoData = selectedLead.seo_data || {};
      const pagespeedMobile = seoData.pagespeed_mobile !== null && seoData.pagespeed_mobile !== undefined 
        ? String(seoData.pagespeed_mobile) : "N/A";
      const seozoomAuthority = seoData.seozoom_za !== null && seoData.seozoom_za !== undefined
        ? String(seoData.seozoom_za) : "N/A";
      const seozoomTraffic = seoData.seozoom_traffic !== null && seoData.seozoom_traffic !== undefined
        ? String(seoData.seozoom_traffic).replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "N/A";
      
      // Replace all placeholders EXCEPT custom links (those will be replaced by backend with tracking)
      const personalizedSubject = emailSubject
        .replace(/\{nome\}/gi, selectedLead.name || "")
        .replace(/\{\{EMAIL\}\}/gi, selectedLead.email || "")
        .replace(/\{\{PAGESPEED_MOBILE\}\}/gi, pagespeedMobile)
        .replace(/\{\{SEOZOOM_AUTHORITY\}\}/gi, seozoomAuthority)
        .replace(/\{\{SEOZOOM_TRAFFIC\}\}/gi, seozoomTraffic)
        .replace(/\{\{LINK_CALENDAR_BNI\}\}/gi, userSettings.calendar_link_bni || "")
        .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/gi, userSettings.calendar_link_standard || "")
        .replace(/\{\{LINK_CALENDAR\}\}/gi, userSettings.calendar_link_bni || "");
        // Note: LINK_TRACKING, LINK_VIDEO, LINK_CONSULENZA, LINK_RISORSA, LINK_CHIAMAMI 
        // are NOT replaced here - they will be replaced by backend with tracked URLs
        
      const personalizedBody = emailBody
        .replace(/\{nome\}/gi, selectedLead.name || "")
        .replace(/\{\{EMAIL\}\}/gi, selectedLead.email || "")
        .replace(/\{\{PAGESPEED_MOBILE\}\}/gi, pagespeedMobile)
        .replace(/\{\{SEOZOOM_AUTHORITY\}\}/gi, seozoomAuthority)
        .replace(/\{\{SEOZOOM_TRAFFIC\}\}/gi, seozoomTraffic)
        .replace(/\{\{LINK_CALENDAR_BNI\}\}/gi, userSettings.calendar_link_bni || "")
        .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/gi, userSettings.calendar_link_standard || "")
        .replace(/\{\{LINK_CALENDAR\}\}/gi, userSettings.calendar_link_bni || "");
        // Note: LINK_TRACKING, LINK_VIDEO, LINK_CONSULENZA, LINK_RISORSA, LINK_CHIAMAMI 
        // are NOT replaced here - they will be replaced by backend with tracked URLs
      
      await axios.post(`${API}/leads/send-email`, {
        lead_id: selectedLead._id,
        to_email: selectedLead.email,
        subject: personalizedSubject,
        body: personalizedBody,
        group_id: groupId,
        lead_name: selectedLead.name,
        custom_links: customLinks,
        base_url: window.location.origin.replace('-preview.', '-').replace(':3000', '')
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(prev => prev.map(l => 
        l._id === selectedLead._id ? { ...l, email_count: (l.email_count || 0) + 1 } : l
      ));
      
      toast.success("Email inviata con successo");
      setEmailDialogOpen(false);
    } catch (error) {
      console.error("Send email error:", error);
      toast.error("Errore nell'invio dell'email");
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  const handleDeleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(prev => prev.filter(l => l._id !== leadId));
      toast.success("Lead eliminato");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Errore nell'eliminazione");
    }
  };
  
  const handleExportCSV = async () => {
    if (leads.length === 0) {
      toast.warning("Nessun lead da esportare");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/leads/export`, leads, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${group?.name || 'leads'}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV esportato");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Errore nell'esportazione");
    }
  };
  
  const getScoreColor = (score) => {
    if (score === null || score === undefined) return "text-gray-400";
    if (score >= 90) return "text-green-500";
    if (score >= 50) return "text-orange-500";
    return "text-red-500";
  };
  
  const getPreviewHtml = () => {
    return emailBody
      .replace(/\{nome\}/gi, "Mario Rossi")
      .replace(/\{attivita\}/gi, "Studio Esempio")
      .replace(/\{\{EMAIL\}\}/gi, "mario.rossi@esempio.it")
      .replace(/\{\{PAGESPEED_MOBILE\}\}/gi, "85")
      .replace(/\{\{SEOZOOM_AUTHORITY\}\}/gi, "32")
      .replace(/\{\{SEOZOOM_TRAFFIC\}\}/gi, "1.250")
      .replace(/\{\{LINK_CALENDAR_BNI\}\}/gi, userSettings.calendar_link_bni || "https://calendar.google.com/bni...")
      .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/gi, userSettings.calendar_link_standard || "https://calendar.google.com/standard...")
      .replace(/\{\{LINK_CALENDAR\}\}/gi, userSettings.calendar_link_bni || "https://calendar.google.com/...")
      .replace(/\{\{LINK_TRACKING\}\}/gi, customLinks.tracking || "https://tracking.esempio.it/...")
      .replace(/\{\{LINK_VIDEO\}\}/gi, customLinks.video || "https://video.esempio.it/...")
      .replace(/\{\{LINK_CONSULENZA\}\}/gi, customLinks.consulenza || "https://consulenza.esempio.it/...")
      .replace(/\{\{LINK_RISORSA\}\}/gi, customLinks.risorsa || "https://risorsa.esempio.it/...")
      .replace(/\{\{LINK_CHIAMAMI\}\}/gi, customLinks.chiamami || "https://chiamami.esempio.it/...");
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="lead-group-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/leads")}
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Gruppi Lead
            </Button>
            <div className="h-px w-6 bg-slate-300 rotate-90" />
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-orange-500 flex items-center justify-center">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight">{group?.name}</h1>
                <p className="text-xs text-muted-foreground">{leads.length} lead</p>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Esporta CSV
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="leads" className="gap-2">
              <Users className="h-4 w-4" />
              Lead ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="marketing" className="gap-2">
              <Mail className="h-4 w-4" />
              Marketing
            </TabsTrigger>
            <TabsTrigger value="template" className="gap-2">
              <FileCode className="h-4 w-4" />
              Modello Email
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2" onClick={() => !analytics && loadAnalytics()}>
              <BarChart2 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* LEADS TAB */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Lead del Gruppo</CardTitle>
                <CardDescription>
                  Ricerca originale: {group?.search_query || "N/A"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Nessun lead in questo gruppo</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome / Indirizzo</TableHead>
                          <TableHead>Telefono</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Sito Web</TableHead>
                          <TableHead>Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead._id}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-semibold">{lead.name}</span>
                                {lead.address && (
                                  <span className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                    <MapPin className="h-3 w-3" />
                                    {lead.address}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {lead.phone ? (
                                <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {lead.phone}
                                </a>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {lead.email ? (
                                <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {lead.email}
                                </a>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {lead.website ? (
                                <a 
                                  href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  <Globe className="h-3 w-3" />
                                  <span className="truncate max-w-[120px]">
                                    {lead.website.replace(/^https?:\/\//, '')}
                                  </span>
                                </a>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteLead(lead._id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Elimina Lead</TooltipContent>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MARKETING TAB */}
          <TabsContent value="marketing" className="space-y-6">
            {/* Email Extraction */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-blue-500" />
                    Estrazione Email
                  </CardTitle>
                  <CardDescription>
                    Estrai automaticamente le email dai siti web o inseriscile manualmente
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleBulkExtractEmails}
                  disabled={bulkExtracting}
                >
                  {bulkExtracting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Estrai Tutte
                </Button>
              </CardHeader>
            </Card>

            {/* SEO Analysis & Email Send */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-green-500" />
                    Analisi SEO e Invio Email
                  </CardTitle>
                  <CardDescription>
                    Analizza i siti web e invia email ai lead
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleBulkAnalyze}
                  disabled={bulkAnalyzing}
                >
                  {bulkAnalyzing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <BarChart3 className="h-4 w-4 mr-2" />
                  )}
                  Analizza Tutti SEO
                </Button>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Nessun lead da analizzare
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Sito Web</TableHead>
                          <TableHead className="bg-green-50">SEO</TableHead>
                          <TableHead className="text-center">Inviate</TableHead>
                          <TableHead>Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => {
                          const seo = lead.seo_data || {};
                          const isAnalyzing = analyzingWebsite === lead._id;
                          const isExtracting = extractingEmail === lead._id;
                          const isEditing = editingEmailId === lead._id;
                          
                          return (
                            <TableRow key={lead._id}>
                              <TableCell className="font-medium">{lead.name}</TableCell>
                              <TableCell>
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={editingEmailValue}
                                      onChange={(e) => setEditingEmailValue(e.target.value)}
                                      className="h-7 text-sm w-40"
                                      placeholder="email@esempio.it"
                                      autoFocus
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-green-600"
                                      onClick={() => handleSaveEmail(lead._id)}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-red-500"
                                      onClick={handleCancelEditEmail}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : lead.email ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-green-600 text-sm">{lead.email}</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                                          onClick={() => handleStartEditEmail(lead)}
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Modifica email</TooltipContent>
                                    </Tooltip>
                                    {lead.website && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 text-blue-500"
                                            onClick={() => handleExtractEmail(lead)}
                                            disabled={isExtracting}
                                          >
                                            {isExtracting ? (
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : (
                                              <RefreshCw className="h-3 w-3" />
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Riesegui estrazione</TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    {lead.website ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs text-blue-600"
                                        onClick={() => handleExtractEmail(lead)}
                                        disabled={isExtracting}
                                      >
                                        {isExtracting ? (
                                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                          <Search className="h-3 w-3 mr-1" />
                                        )}
                                        Estrai
                                      </Button>
                                    ) : null}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 w-7 p-0 text-slate-500"
                                          onClick={() => handleStartEditEmail(lead)}
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Inserisci manualmente</TooltipContent>
                                    </Tooltip>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {lead.website ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <a 
                                        href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 text-blue-600"
                                      >
                                        <Globe className="h-4 w-4" />
                                      </a>
                                    </TooltipTrigger>
                                    <TooltipContent>{lead.website.replace(/^https?:\/\//, '')}</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell className="bg-green-50/50">
                                {!lead.website ? (
                                  <span className="text-slate-400 text-xs">-</span>
                                ) : isAnalyzing ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                ) : !seo.domain ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => handleAnalyzeSEO(lead)}
                                  >
                                    <BarChart3 className="h-3 w-3 mr-1" />
                                    Analizza
                                  </Button>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center">
                                          <Smartphone className={`h-3 w-3 ${getScoreColor(seo.pagespeed_mobile)}`} />
                                          <span className={`text-xs font-semibold ${getScoreColor(seo.pagespeed_mobile)}`}>
                                            {seo.pagespeed_mobile ?? "-"}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>Mobile</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center">
                                          <Monitor className={`h-3 w-3 ${getScoreColor(seo.pagespeed_desktop)}`} />
                                          <span className={`text-xs font-semibold ${getScoreColor(seo.pagespeed_desktop)}`}>
                                            {seo.pagespeed_desktop ?? "-"}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>Desktop</TooltipContent>
                                    </Tooltip>
                                    {seo.seozoom_za !== undefined && seo.seozoom_za !== null && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex flex-col items-center">
                                            <span className="text-xs text-slate-500">ZA</span>
                                            <span className="text-xs font-semibold text-purple-600">
                                              {seo.seozoom_za}
                                            </span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>SEOZoom Authority</TooltipContent>
                                      </Tooltip>
                                    )}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center">
                                          <TrendingUp className="h-3 w-3 text-blue-500" />
                                          <span className="text-xs font-semibold text-blue-600">
                                            {seo.seozoom_traffic 
                                              ? (seo.seozoom_traffic > 1000 
                                                ? `${(seo.seozoom_traffic / 1000).toFixed(1)}k` 
                                                : seo.seozoom_traffic)
                                              : "-"}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>Traffico stimato mensile</TooltipContent>
                                    </Tooltip>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0"
                                      onClick={() => handleAnalyzeSEO(lead)}
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                  {lead.email_count || 0}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-8 w-8 p-0 ${lead.email ? 'text-blue-600' : 'text-slate-300'}`}
                                      onClick={() => handleOpenEmailDialog(lead)}
                                      disabled={!lead.email}
                                    >
                                      <Send className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {lead.email ? "Invia Email" : "Inserisci prima l'email"}
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteLead(lead._id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Elimina Lead</TooltipContent>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EMAIL TEMPLATE TAB */}
          <TabsContent value="template">
            {/* Placeholders Info Card */}
            <Card className="mb-4 border-border/60 bg-blue-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="h-4 w-4 text-blue-600" />
                  Placeholder Disponibili
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{nome}"}</code> - Nome del lead
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{attivita}"}</code> - Nome attività
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{{EMAIL}}"}</code> - Email del contatto
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{{PAGESPEED_MOBILE}}"}</code> - Punteggio Mobile
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{{SEOZOOM_AUTHORITY}}"}</code> - SEOZoom ZA
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{{SEOZOOM_TRAFFIC}}"}</code> - Traffico Stimato
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{{LINK_CALENDAR_BNI}}"}</code> - Link Calendario BNI
                  </span>
                  <span className="bg-white px-2 py-1 rounded border text-xs">
                    <code>{"{{LINK_CALENDAR_STANDARD}}"}</code> - Link Calendario Standard
                  </span>
                  <span className="bg-orange-100 px-2 py-1 rounded border border-orange-300 text-xs">
                    <code>{"{{LINK_TRACKING}}"}</code> - Link Tracking
                  </span>
                  <span className="bg-orange-100 px-2 py-1 rounded border border-orange-300 text-xs">
                    <code>{"{{LINK_VIDEO}}"}</code> - Link Video
                  </span>
                  <span className="bg-orange-100 px-2 py-1 rounded border border-orange-300 text-xs">
                    <code>{"{{LINK_CONSULENZA}}"}</code> - Link Consulenza
                  </span>
                  <span className="bg-orange-100 px-2 py-1 rounded border border-orange-300 text-xs">
                    <code>{"{{LINK_RISORSA}}"}</code> - Link Risorsa
                  </span>
                  <span className="bg-orange-100 px-2 py-1 rounded border border-orange-300 text-xs">
                    <code>{"{{LINK_CHIAMAMI}}"}</code> - Link Chiamami
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Custom Links Card */}
            <Card className="mb-4 border-border/60 border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Link2 className="h-4 w-4 text-orange-500" />
                  Link Personalizzati per questo Gruppo
                </CardTitle>
                <CardDescription>
                  Inserisci i link che verranno sostituiti ai placeholder nel template
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Tracking</Label>
                    <Input
                      value={customLinks.tracking}
                      onChange={(e) => setCustomLinks(prev => ({ ...prev, tracking: e.target.value }))}
                      placeholder="https://tracking.example.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Video</Label>
                    <Input
                      value={customLinks.video}
                      onChange={(e) => setCustomLinks(prev => ({ ...prev, video: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Consulenza</Label>
                    <Input
                      value={customLinks.consulenza}
                      onChange={(e) => setCustomLinks(prev => ({ ...prev, consulenza: e.target.value }))}
                      placeholder="https://calendly.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Risorsa</Label>
                    <Input
                      value={customLinks.risorsa}
                      onChange={(e) => setCustomLinks(prev => ({ ...prev, risorsa: e.target.value }))}
                      placeholder="https://drive.google.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Chiamami</Label>
                    <Input
                      value={customLinks.chiamami}
                      onChange={(e) => setCustomLinks(prev => ({ ...prev, chiamami: e.target.value }))}
                      placeholder="https://wa.me/39123456789"
                      className="text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileCode className="h-5 w-5 text-orange-500" />
                      Modello Email HTML
                    </CardTitle>
                    <CardDescription>
                      Crea il template email in HTML per questo gruppo. Usa i placeholder sopra per personalizzare le email.
                    </CardDescription>
                  </div>
                  <Button onClick={handleSaveTemplate} disabled={isSavingTemplate}>
                    {isSavingTemplate ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salva
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Oggetto Email</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Oggetto dell'email..."
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Editor */}
                  <div className="space-y-2">
                    <Label>Codice HTML</Label>
                    <Textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      className="min-h-[400px] font-mono text-sm"
                      placeholder="Inserisci il codice HTML del template..."
                    />
                  </div>

                  {/* Preview */}
                  <div className="space-y-2">
                    <Label>Anteprima</Label>
                    <div 
                      className="border rounded-lg p-4 min-h-[400px] bg-white overflow-auto"
                      dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                    />
                  </div>
                </div>
                
                {/* Test Email Section */}
                <div className="border-t pt-4 mt-4">
                  <Label className="mb-2 block">Invia Email di Test</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="email@esempio.it"
                      value={testEmailRecipient}
                      onChange={(e) => setTestEmailRecipient(e.target.value)}
                      className="max-w-xs"
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (!testEmailRecipient) {
                          toast.error("Inserisci un indirizzo email");
                          return;
                        }
                        setIsSendingTestEmail(true);
                        try {
                          await axios.post(`${API}/email/test`, {
                            to_email: testEmailRecipient,
                            subject: emailSubject.replace(/\{nome\}/gi, "Mario Rossi"),
                            body: getPreviewHtml()
                          }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success("Email di test inviata!");
                        } catch (error) {
                          toast.error(getErrorMessage(error, "Errore nell'invio dell'email"));
                        } finally {
                          setIsSendingTestEmail(false);
                        }
                      }}
                      disabled={isSendingTestEmail}
                    >
                      {isSendingTestEmail ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Invia Test
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <AnalyticsDashboard
              analytics={analytics}
              isLoading={isLoadingAnalytics}
              onRefresh={loadAnalytics}
              onExport={exportAnalytics}
              entityName="Lead"
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invia Email a {selectedLead?.name}</DialogTitle>
            <DialogDescription>
              Destinatario: {selectedLead?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Oggetto</Label>
              <div className="p-2 border rounded bg-slate-50 text-sm">
                {emailSubject.replace(/\{nome\}/gi, selectedLead?.name || "")}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Anteprima Email</Label>
              <div 
                className="border rounded-lg p-4 bg-white overflow-auto max-h-[300px]"
                dangerouslySetInnerHTML={{ 
                  __html: emailBody.replace(/\{nome\}/gi, selectedLead?.name || "") 
                }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendEmail} 
              disabled={isSendingEmail}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {isSendingEmail ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Invia Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default LeadGroupDetailPage;
